import {
  DateTimeFormatter,
  Duration,
  LocalTime,
  OffsetDateTime,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import "@js-joda/timezone";
import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import { CompanyGigShift, GigType } from "model/Gig";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { TextInputWithLabel } from "../../general/textInput/textInput.component";
import { TimePickerWithLabel } from "../../general/timePicker/timePickerWithLabel.component";
import { SimpleRow } from "../../organizationalStructure/organizationalStructure.styled";
import { DropDownRow } from "../gigDetail/gigDetail.styled";
import { Column, ListContainer } from "./createGigModal.styled";

type Props = {
  setShiftListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shiftListOpen: boolean;
  shifts: CompanyGigShift[];
  setFormData: React.Dispatch<
    React.SetStateAction<{
      description: string;
      shifts: CompanyGigShift[];
      type: GigType;
    }>
  >;
  formData: {
    description: string;
    shifts: CompanyGigShift[];
    type: GigType;
  };
  overlapping: { worker: any; shifts: CompanyGigShift[] }[];
  showOverlappingError: boolean;
};

// HH:mm[,ss] eller ISO med offset (inkl. Z)
const toLocalTime = (value: string, zone: ZoneId): LocalTime => {
  if (!value) throw new Error("Invalid time");
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) return LocalTime.parse(value);
  // ISO OffsetDateTime (+hh:mm eller Z)
  const odt = OffsetDateTime.parse(value);
  return odt.atZoneSameInstant(zone).toLocalTime();
};

const formatHM = (totalMinutes: number) => {
  const sign = totalMinutes < 0 ? "-" : "";
  const abs = Math.abs(totalMinutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h > 0 && m > 0) return `${sign}${h}h ${m}min`;
  if (h > 0) return `${sign}${h}h`;
  return `${sign}${m}min`;
};

const shiftKey = (s: CompanyGigShift) => `${s.startTime}|${s.endTime}`;

export const ShiftList: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const companyTimeZone =
    companyState.companySettings.find((s: any) => s.key === "CompanyTimeZone")
      ?.value ?? "Europe/Stockholm";

  const zoneId = useMemo(() => ZoneId.of(companyTimeZone), [companyTimeZone]);

  const DATE_FMT = useMemo(() => DateTimeFormatter.ofPattern("d/M"), []);
  const formatShiftDate = useCallback(
    (isoString: string) =>
      OffsetDateTime.parse(isoString)
        .atZoneSameInstant(zoneId)
        .format(DATE_FMT),
    [zoneId, DATE_FMT]
  );

  const overlapMap = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const item of props.overlapping) {
      for (const s of item.shifts) {
        const key = shiftKey(s);
        const arr = map.get(key) ?? [];
        arr.push(item.worker);
        map.set(key, arr);
      }
    }
    return map;
  }, [props.overlapping]);

  const updateShift = useCallback(
    (idx: number, patch: Partial<CompanyGigShift>) => {
      props.setFormData((prev) => ({
        ...prev,
        shifts: prev.shifts.map((s, i) =>
          i === idx
            ? {
                ...s,
                ...patch,
                breakStartTime: null,
                breakEndTime: null,
              }
            : s
        ),
      }));
    },
    [props.setFormData]
  );

  const sortedShifts = useMemo(() => {
    type Prepared = {
      shift: CompanyGigShift;
      originalIndex: number;
      startZdt: ZonedDateTime;
      endZdt: ZonedDateTime;
    };

    const prepared: Prepared[] = props.shifts.map((s, i) => {
      const startZdt = OffsetDateTime.parse(s.startTime).atZoneSameInstant(
        zoneId
      );
      const endZdt = s.endTime.includes("T")
        ? OffsetDateTime.parse(s.endTime).atZoneSameInstant(zoneId)
        : startZdt
            .withHour(toLocalTime(s.endTime, zoneId).hour())
            .withMinute(toLocalTime(s.endTime, zoneId).minute())
            .withSecond(0)
            .withNano(0);

      return { shift: s, originalIndex: i, startZdt, endZdt };
    });

    prepared.sort((a, b) => {
      // Datum
      const aDate = a.startZdt.toLocalDate().toString();
      const bDate = b.startZdt.toLocalDate().toString();
      if (aDate !== bDate) return aDate < bDate ? -1 : 1;

      // Starttid
      const aStartNanos = a.startZdt.toLocalTime().toNanoOfDay();
      const bStartNanos = b.startZdt.toLocalTime().toNanoOfDay();
      if (aStartNanos !== bStartNanos) return aStartNanos - bStartNanos;

      // Sluttid
      const aEndNanos = a.endZdt.toLocalTime().toNanoOfDay();
      const bEndNanos = b.endZdt.toLocalTime().toNanoOfDay();
      if (aEndNanos !== bEndNanos) return aEndNanos - bEndNanos;

      return 0;
    });

    return prepared;
  }, [props.shifts, zoneId]);

  const totalShiftTime = useMemo(() => {
    let totalMinutes = 0;

    for (const shift of props.shifts) {
      const startLT = toLocalTime(shift.startTime, zoneId);
      const endLT = toLocalTime(shift.endTime, zoneId);
      let diff = Duration.between(startLT, endLT).toMinutes();
      if (diff < 0) diff += 24 * 60; // över midnatt

      const breakMin = Number(shift.breakTime) || 0;
      totalMinutes += diff - breakMin;
    }

    return formatHM(totalMinutes);
  }, [props.shifts, zoneId]);

  return (
    <>
      <DropDownRow onClick={() => props.setShiftListOpen(!props.shiftListOpen)}>
        <Text fontSize={FontSize.Standard}>{`${t(
          "CreateShift.AddedShifts"
        )}: (${props.shifts.length}) st - ${t(
          "CreateShift.TotalTime"
        )}: ${totalShiftTime}`}</Text>
        <CustomIcon
          name={props.shiftListOpen ? "upward_arrow" : "downward_arrow"}
          size="24px"
          color={Color.BurntSienna}
          style={{ marginTop: 10 }}
        />
      </DropDownRow>

      {props.shiftListOpen && (
        <ListContainer>
          {sortedShifts.map(({ shift, originalIndex, startZdt }, index) => {
            const key = shiftKey(shift);
            const overlappingWorkers = overlapMap.get(key) ?? [];
            const hasOverlapping = overlappingWorkers.length > 0;

            const weekdayKey = startZdt.dayOfWeek().name();
            const weekdayLabel = t(`WeekDays.${weekdayKey}SHORT`) as string;

            return (
              <SimpleRow
                key={`${originalIndex}-${shift.startTime}-${shift.endTime}`}
                style={{
                  justifyContent: "space-between",
                  borderBottom: `1px solid ${Color.SeaBlue200}`,
                  width: "95%",
                  height: 70,
                  position: "relative",
                }}
                onMouseLeave={() => {
                  if (hoveredIdx === index) setHoveredIdx(null);
                }}
              >
                {props.showOverlappingError && hasOverlapping && (
                  <>
                    <span
                      onMouseEnter={() => setHoveredIdx(index)}
                      onFocus={() => setHoveredIdx(index)}
                      onBlur={() =>
                        setHoveredIdx((prev) => (prev === index ? null : prev))
                      }
                      onMouseDown={() => setHoveredIdx(null)}
                      tabIndex={0}
                      style={{ display: "inline-flex", outline: "none" }}
                      aria-haspopup="true"
                      aria-expanded={hoveredIdx === index}
                      aria-label={t("CreateShift.OverlappingShifts") as string}
                    >
                      <CustomIcon
                        name="alert"
                        size="44px"
                        color={Color.LighterDestructive}
                        padding="10px"
                        style={{ marginTop: 4, cursor: "pointer" }}
                      />
                    </span>

                    {hoveredIdx === index && (
                      <div
                        style={{
                          position: "absolute",
                          top: 48,
                          left: 0,
                          backgroundColor: Color.White,
                          border: `1px solid ${Color.SeaBlue200}`,
                          borderRadius: 8,
                          padding: "10px 12px",
                          boxShadow:
                            "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
                          zIndex: 100,
                          maxWidth: 260,
                        }}
                        role="dialog"
                        aria-label={
                          t("CreateShift.OverlappingShifts") as string
                        }
                      >
                        <Text
                          fontSize={FontSize.Small}
                          style={{ marginBottom: 2 }}
                          fontFamily={FontFamily.MontserratSemiBold}
                        >
                          {t("CreateShift.PeopleWithOverlappingShifts")}
                        </Text>
                        {overlappingWorkers.map((w: any, i: number) => (
                          <Text key={i} fontSize={FontSize.Tiny}>
                            {`${w.firstName} ${w.lastName}`}
                          </Text>
                        ))}
                      </div>
                    )}
                  </>
                )}

                <Column>
                  <Text style={{ marginBottom: 0 }} fontSize={FontSize.Tiny}>
                    {t("CreateShift.Date")}
                  </Text>
                  <Text style={{ marginTop: 0 }}>
                    {`${weekdayLabel} ${formatShiftDate(shift.startTime)}`}
                  </Text>
                </Column>

                <SimpleRow>
                  <TimePickerWithLabel
                    label={t("StampClock.StartTime")}
                    value={shift.startTime}
                    onChange={(value: any) =>
                      updateShift(originalIndex, { startTime: value })
                    }
                    inputSize="small"
                    width={70}
                    marginTop={5}
                    marginBottom={5}
                    marginRight={0}
                  />
                  <Text>{"-"}</Text>
                  <TimePickerWithLabel
                    label={t("StampClock.EndTime")}
                    value={shift.endTime}
                    onChange={(value: any) =>
                      updateShift(originalIndex, { endTime: value })
                    }
                    inputSize="small"
                    width={70}
                    marginTop={5}
                    marginBottom={5}
                  />
                </SimpleRow>

                <TextInputWithLabel
                  label={t("CreateShift.Break")}
                  value={shift.breakTime?.toString() ?? "0"}
                  onChange={(e) => {
                    const num = (e.target as HTMLInputElement).valueAsNumber;
                    updateShift(originalIndex, {
                      breakTime: Number.isNaN(num) ? 0 : num,
                    });
                  }}
                  inputSize="small"
                  type="number"
                  marginTop={5}
                  marginBottom={5}
                  width={100}
                />

                <TextInputWithLabel
                  label={t("CreateShift.DeductionAmount")}
                  value={shift.deductionAmount?.toString() ?? undefined}
                  onChange={(e) => {
                    const num = (e.target as HTMLInputElement).valueAsNumber;
                    updateShift(originalIndex, {
                      deductionAmount: Number.isNaN(num) ? 0 : num,
                    });
                  }}
                  type="number"
                  inputSize="small"
                  marginTop={5}
                  marginBottom={5}
                  width={150}
                  marginRight={0}
                />

                <CustomIcon
                  name="trashcan"
                  size="40px"
                  color={Color.LighterDestructive}
                  padding="10px"
                  hover
                  onClick={() => {
                    props.setFormData((prev) => ({
                      ...prev,
                      shifts: prev.shifts.filter((_, i) => i !== originalIndex),
                    }));
                    setHoveredIdx((prev) => (prev === index ? null : prev));
                  }}
                />
              </SimpleRow>
            );
          })}
        </ListContainer>
      )}
    </>
  );
};
